/**
 * partition.ts — pure, defensive hierarchy logic for response widgets (RP-2714).
 *
 * The SERVER decides the hierarchy (which widget is `primary`); the client only
 * RENDERS it — it never recomputes which card is primary. This module drops
 * anything with an unknown `class` or `type` (forward-compat / untrusted input)
 * and splits the survivors into a primary + compact secondaries, or an
 * "all-compact" set when no widget is primary. Mirrors the never-throw style of
 * lib/agents.ts `reduceTrace`.
 */
import { CopilotWidget, KnownWidgetType, KnownSuggestionType } from '../lib/copilotTypes'

/** the six v1 display types the client can render; everything else → nothing. */
export const KNOWN_WIDGET_TYPES: readonly KnownWidgetType[] = [
  'review_list',
  'review_stats',
  'booking_list',
  'booking_stats',
  'payment_list',
  'payment_stats'
]

/** the v1 suggestion types (class 'suggestion'); unknown suggestion type → nothing. */
export const KNOWN_SUGGESTION_TYPES: readonly KnownSuggestionType[] = ['csm_connect']

const isKnownType = (type: unknown): type is KnownWidgetType =>
  typeof type === 'string' && (KNOWN_WIDGET_TYPES as readonly string[]).includes(type)

const isKnownSuggestionType = (type: unknown): type is KnownSuggestionType =>
  typeof type === 'string' && (KNOWN_SUGGESTION_TYPES as readonly string[]).includes(type)

/**
 * A widget renders only when it is the display class AND a known type. Unknown
 * class or unknown type → render nothing (never throw), so the backend can add
 * classes/types without a lockstep client release.
 */
export const isRenderableWidget = (w: unknown): w is CopilotWidget => {
  if (!w || typeof w !== 'object') return false
  const widget = w as CopilotWidget
  return widget.class === 'display' && isKnownType(widget.type)
}

/**
 * A suggestion renders only when it is the suggestion class AND a known
 * suggestion type ('csm_connect'). It is routed SEPARATELY from display widgets
 * (it is neither a stat chip nor a display card) — never through the
 * primary/compact logic. Unknown suggestion type → dropped (never throw).
 */
export const isRenderableSuggestion = (w: unknown): w is CopilotWidget => {
  if (!w || typeof w !== 'object') return false
  const widget = w as CopilotWidget
  return widget.class === 'suggestion' && isKnownSuggestionType(widget.type)
}

/** a widget's domain from its type prefix ('review_stats' → 'review'); '' when unknown. */
const compactDomain = (type: unknown): string => (typeof type === 'string' ? type.split('_')[0] : '')

/** stats representations carry the headline number; lists just count rows. */
const isStatsType = (type: unknown): boolean => typeof type === 'string' && type.endsWith('_stats')

/**
 * Render only ONE compact chip per domain (review/booking/payment): a
 * review_stats + review_list turn shouldn't show two "Reviews" chips. Keeps the
 * FIRST widget of each domain in wire order, but upgrades to the stats one if a
 * later same-domain widget is stats (the number is higher-value than "Reviews 1").
 * Compact-row rendering policy only — never touches which widget is primary.
 */
export const dedupeCompactByDomain = (widgets: CopilotWidget[]): CopilotWidget[] => {
  const indexByDomain: Record<string, number> = {}
  const result: CopilotWidget[] = []
  widgets.forEach((w) => {
    const dom = compactDomain(w.type)
    const seen = indexByDomain[dom]
    if (seen === undefined) {
      indexByDomain[dom] = result.length
      result.push(w)
    } else if (isStatsType(w.type) && !isStatsType(result[seen].type)) {
      result[seen] = w
    }
  })
  return result
}

export interface WidgetPartition {
  /** the full card; undefined on all-compact turns or when nothing is renderable. */
  primary?: CopilotWidget
  /** compact widgets: secondaries (when a primary exists) OR every survivor (all-compact). */
  compact: CopilotWidget[]
  /** true when no widget is primary → render every survivor as a compact chip. */
  allCompact: boolean
  /** renderable suggestion widgets (class 'suggestion'), rendered BELOW the display widgets. */
  suggestions: CopilotWidget[]
  /** false when nothing renders → the renderer emits null. */
  hasContent: boolean
}

/** the display hierarchy fields, split out so the suggestion routing can't perturb it. */
interface DisplaySplit {
  primary?: CopilotWidget
  compact: CopilotWidget[]
  allCompact: boolean
  hasContent: boolean
}

/**
 * Split validated DISPLAY widgets into the render hierarchy, ORDER PRESERVED
 * (primary first, then secondaries in wire order):
 *   - one widget with primary:true → it is the full card, the rest are compact secondaries;
 *   - no primary (all false/absent) → every survivor renders compact ("all-compact" turn).
 * Defensive against a malformed turn carrying >1 primary: the first wins, the
 * others degrade to secondaries (still 1 full + rest compact).
 */
const splitDisplay = (survivors: CopilotWidget[]): DisplaySplit => {
  if (!survivors.length) return { compact: [], allCompact: false, hasContent: false }

  const primaryIdx = survivors.findIndex((w) => w.primary === true)
  if (primaryIdx === -1) return { compact: dedupeCompactByDomain(survivors), allCompact: true, hasContent: true }

  return {
    primary: survivors[primaryIdx],
    compact: dedupeCompactByDomain(survivors.filter((_, i) => i !== primaryIdx)),
    allCompact: false,
    hasContent: true
  }
}

/**
 * Partition a turn's widgets into the display hierarchy PLUS a separate
 * `suggestions` bucket. Suggestions (class 'suggestion') never flow through the
 * display primary/compact logic — they render below the data as a follow-on
 * offer. The display split stays byte-identical to before (regression-guarded);
 * `hasContent` is true when EITHER a display widget or a suggestion survives, so
 * a suggestion-only turn still renders.
 */
export const partitionWidgets = (widgets: unknown): WidgetPartition => {
  const list = Array.isArray(widgets) ? widgets : []
  const survivors = list.filter(isRenderableWidget)
  const suggestions = list.filter(isRenderableSuggestion)

  const display = splitDisplay(survivors)
  return {
    primary: display.primary,
    compact: display.compact,
    allCompact: display.allCompact,
    suggestions,
    hasContent: display.hasContent || suggestions.length > 0
  }
}
